import _ from "lodash";
import React, { useEffect, useRef, useMemo } from "react";
import { connect } from "react-redux";
import { ASSESSMENT_TOOLS } from "@toddle-edu/common-utils";
import { graphql, compose } from "lib/apolloClient";
import classes from "./ViewerComponent.scss";
import RubricTable from "Rubric/components/RubricTable";
import {
  getAssessmentToolsDetailsByStudentAssignmentIdQuery,
  getAssessmentToolsDetailsByPostIdQuery,
} from "modules/CommonQuery";
import { getAssessmentToolsByQuestionSubmissionIdQuery } from "Worksheet/modules/WorksheetQueries";
import { QUESTION_SUBMISSION } from "Worksheet/modules/WorksheetConstants";
import { getAssessmentToolsByQuestionSubmissionIdFromCache } from "Worksheet/modules/WorksheetGraphqlHelpers";
import {
  getAssessmentToolsDetailsByStudentAssignmentIdFromCache,
  getAssessmentToolsDetailsByPostIdFromCache,
} from "modules/CommonGraphqlHelpers";
import {
  getAssessmentToolText,
  getScoreValueElementRatings,
} from "ClassRoom/modules/utils";
import ChecklistTable from "Checklist/components/ChecklistTable";
import SinglePointRubricTable from "SinglePointRubric/components/SinglePointRubricTable";
import AIAnalysisScale from "UnitPlans/components/IBcomponents/AIAnalysisScale";
import MYPObjectiveRubric from "UnitPlans/components/IBcomponents/AssessmentTool/MYPObjectiveRubric";
import { getMYPObjectiveRubricOptions } from "modules/Services";
import { updateField } from "modules/FieldUpdateThunks";
import CriteriaRubricAssessmentWrapper from "UnitPlans/components/IBcomponents/CriteriaRubricAssessmentWrapper";
import PypEvaluationV2 from "AssessmentEvaluation/components/PypEvaluationV2";
import { getScoreDetailsMemoize } from "ScoreCategories/modules/ScoreCategoriesModule";
import EvaluationStatus from "AssessmentEvaluation/components/EvaluationStatus";
import withLoader from "UIComponents/GqlWithLoader";
import withQueryParamsBasedInterface from "UIComponents/QueryParamsBasedInterfaceHOC";
import AssessmentIBScaleTool from "UnitPlans/components/IBcomponents/AssessmentIBScaleTool";
import {
  ASSESSMENT_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY,
  ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY,
  SCORE_BASED_RUBRIC,
  STANDARD_BASED_RUBRIC,
  GRADE_SCALE_TOOL,
} from "Constants/stringConstants";
import { handleAssessmentToolResponseInCache } from "AssessmentEvaluation/modules/AssessmentEvaluationModule";
import { colors } from "Constants";
import { getUserType } from "modules/CommonSelectors";
import StandardRubric from "AppComponents/StandardRubric";
import AssessmentGradeScaleTool from "AppComponents/AssessmentGradeScaleTool";

const styles = {
  toolStyles: {
    leftContainerStyle: {
      color: colors.textSubtle,
    },
  },
};
const getEvaluationStatusLegend = data => {
  const {
    assessmentToolType,
    isEvaluatedByStudent,
    isTeacherEvaluationEnable,
    userType,
  } = data;
  return (
    <EvaluationStatus
      toolType={assessmentToolType}
      isEvaluatedByStudent={isEvaluatedByStudent}
      isEvaluatedByTeacher={isTeacherEvaluationEnable}
      userType={userType}
    />
  );
};
// Render assessment tool based on type
const AssessmentToolViewer = React.memo(props => {
  const {
    mediaDataAttachment, // object from theatreMode
    assessmentId,
    assessmentToolType,
    assessmentTool, // object from query response ( having row, cols details )
    curriculumProgramType,
    assessmentToolsEvaluationDetails,
    t,
    scoreValue = null,
    maxScore = 0,
    assessmentToolsRefs,
    userType,
    handleAssessmentToolResponseInCache,
    isSharedWithStudent,
    learningCourseIdFromProps: learningCourseId,
  } = props;
  // For assessment tools configurations
  const { assessmentToolText } = getAssessmentToolText({
    t,
    assessmentToolType,
    assessmentTool,
  });

  const isEvaluatedByStudent = _.get(
    mediaDataAttachment,
    "isStudentEvaluationEnable"
  );

  const isTeacherEvaluationEnable = _.get(
    mediaDataAttachment,
    "isTeacherEvaluationEnable"
  );

  const currentToolEvaluationResponse = !_.isUndefined(
    _.get(mediaDataAttachment, "assessmentToolType")
  )
    ? _.get(
        _.find(
          _.get(
            assessmentToolsEvaluationDetails,
            "toolEvaluationResponses",
            []
          ),
          ({ toolId }) =>
            toolId == _.get(assessmentTool, "assessmentToolItem.id")
        ),
        "toolResponses",
        {}
      )
    : [];

  const commentMessage = _.get(
    assessmentToolsEvaluationDetails,
    "commentMessage"
  );

  const postRatedLearningGoalsDetails = _.get(
    props,
    "postRatedLearningGoalsDetails",
    {}
  );

  const setToolRef = ({ ref }) => {
    if (assessmentToolsRefs?.current) {
      assessmentToolsRefs.current[assessmentId] = ref;
    }
  };

  const handleToolRef = ref => setToolRef({ ref });

  const debouncedHandleAssessmentToolResponseInCache = useMemo(
    () => _.debounce(handleAssessmentToolResponseInCache, 300),
    []
  );

  const updateEvaluationCell = ({
    rowId,
    columnId,
    subjectiveResponse,
    type,
    assessmentToolId,
    isDebounce = false,
  }) => {
    const {
      updateField,
      handleAssessmentToolResponseInCache,
      userType,
    } = props;
    const { parentId, parentType, toolType, extraParams, submissionId } = _.get(
      props,
      "assessmentToolsEvaluationDetails",
      {}
    );

    // parent is not allowed to update rubric cell
    if (userType === "parent") {
      return;
    }

    let params = {
      parentId,
      parentType,
      rowId,
      type,
      columnId,
      subjectiveResponse,
      ..._.pick(extraParams, ["assignmentId", "sourceFrom", "courseId"]),
      submissionId,
    };

    if (assessmentToolId) {
      params = {
        ...params,
        assessmentToolId,
        isToolResponsesV2: true,
      };
    }

    if (isDebounce) {
      debouncedHandleAssessmentToolResponseInCache(params);
    } else {
      handleAssessmentToolResponseInCache(params);
    }

    updateField({
      key: `${rowId}_${columnId}`,
      params,
      type: "EVALUATION_TOOL",
      isDebounce: toolType == "SINGLE_POINT_RUBRIC",
    });
  };

  const canCurrentUserEvaluate = () => {
    const userLevelPermission =
      userType == "staff"
        ? isTeacherEvaluationEnable
        : userType == "student"
        ? isEvaluatedByStudent
        : false;
    return (
      userLevelPermission &&
      _.get(assessmentToolsEvaluationDetails, "isEvaluationEnable", false)
    );
  };
  switch (assessmentToolType) {
    case "RUBRIC":
      return (
        <div className={classes.assessmentToolCard} ref={handleToolRef}>
          <div className={"heading-4 p-6 pt-2 text-textSubtle pl-0"}>
            {assessmentToolText}
          </div>
          {getEvaluationStatusLegend({
            assessmentToolType,
            isEvaluatedByStudent,
            isTeacherEvaluationEnable,
            userType,
          })}
          <RubricTable
            mode={"view"}
            rubric={_.get(assessmentTool, "assessmentToolItem")}
            isEvaluatedByStudent={isEvaluatedByStudent}
            {...assessmentToolsEvaluationDetails}
            isEvaluation={canCurrentUserEvaluate()}
            toolEvaluationResponses={currentToolEvaluationResponse}
            updateEvaluationCell={params =>
              updateEvaluationCell({
                ...params,
                assessmentToolId: _.get(
                  assessmentTool,
                  "assessmentToolItem.id"
                ),
                type: "RUBRIC",
              })
            }
            isSharedWithStudent={isSharedWithStudent}
          />
        </div>
      );

    case "CHECKLIST":
      return (
        <div className={classes.assessmentToolCard} ref={handleToolRef}>
          <div
            className={"heading-4 text-subtle p-6 pt-2 text-textSubtle pl-0"}
          >
            {assessmentToolText}
          </div>
          {getEvaluationStatusLegend({
            assessmentToolType,
            isEvaluatedByStudent,
            isTeacherEvaluationEnable,
            userType,
          })}
          <ChecklistTable
            mode={"view"}
            checklist={_.get(assessmentTool, "assessmentToolItem")}
            isEvaluatedByStudent={isEvaluatedByStudent}
            {...assessmentToolsEvaluationDetails}
            isEvaluation={canCurrentUserEvaluate()}
            toolEvaluationResponses={currentToolEvaluationResponse}
            updateEvaluationCell={params =>
              updateEvaluationCell({
                ...params,
                assessmentToolId: _.get(
                  assessmentTool,
                  "assessmentToolItem.id"
                ),
                type: "CHECKLIST",
              })
            }
            isSharedWithStudent={isSharedWithStudent}
          />
        </div>
      );
    case "SINGLE_POINT_RUBRIC":
      return (
        <div className={classes.assessmentToolCard} ref={handleToolRef}>
          <div
            className={"heading-4 text-subtle p-6 pt-2 text-textSubtle pl-0"}
          >
            {assessmentToolText}
          </div>
          {getEvaluationStatusLegend({
            assessmentToolType,
            isEvaluatedByStudent,
            isTeacherEvaluationEnable,
            userType,
          })}
          <SinglePointRubricTable
            mode={"view"}
            rubric={_.get(assessmentTool, "assessmentToolItem")}
            isEvaluatedByStudent={isEvaluatedByStudent}
            {...assessmentToolsEvaluationDetails}
            toolEvaluationResponses={currentToolEvaluationResponse}
            updateEvaluationCell={params =>
              updateEvaluationCell({
                ...params,
                assessmentToolId: _.get(
                  assessmentTool,
                  "assessmentToolItem.id"
                ),
                type: "SINGLE_POINT_RUBRIC",
              })
            }
            isEvaluation={true}
            isEvaluationEnable={canCurrentUserEvaluate()}
          />
        </div>
      );
    case "COMMENT":
      return !_.isNil(commentMessage) && !_.isEmpty(commentMessage) ? (
        <div className={classes.assessmentToolCard} ref={handleToolRef}>
          <div className={"heading-4 p-6 pt-2 text-textSubtle pl-0"}>
            {assessmentToolText}
          </div>
          <div className={classes.commentText}>{commentMessage}</div>
        </div>
      ) : null;
    case "MYP_OBJECTIVE_RUBRIC":
    case "MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC":
      return (
        <div className={classes.assessmentToolCard} ref={handleToolRef}>
          <div className={"heading-4 p-6 pt-2 text-textSubtle pl-0"}>
            {assessmentToolText}
          </div>
          {getEvaluationStatusLegend({
            assessmentToolType,
            isEvaluatedByStudent,
            isTeacherEvaluationEnable,
            userType,
          })}
          <MYPObjectiveRubric
            assessmentTool={_.get(assessmentTool, "assessmentToolItem")}
            mode={"view"}
            {...assessmentToolsEvaluationDetails}
            isEvaluated={_.get(
              assessmentToolsEvaluationDetails,
              "isEvaluated",
              true
            )}
            isEvaluatedByStudent={isEvaluatedByStudent}
            options={getMYPObjectiveRubricOptions({
              unitPlanFields: _.get(
                assessmentToolsEvaluationDetails,
                "unitPlanFields"
              ),
              assessmentFields: _.get(
                assessmentToolsEvaluationDetails,
                "assessmentFields"
              ),
              field_list: _.get(assessmentToolsEvaluationDetails, "field_list"),
              measureAssessingValue: _.includes(
                [
                  "MYP_OBJECTIVE_RUBRIC",
                  "MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC",
                ],
                assessmentToolType
              )
                ? assessmentToolType
                : undefined,
            })}
            toolEvaluationResponses={currentToolEvaluationResponse}
            updateEvaluationCell={params =>
              updateEvaluationCell({
                ...params,
                assessmentToolId: _.get(
                  assessmentTool,
                  "assessmentToolItem.id"
                ),
                type: assessmentToolType,
              })
            }
            isEvaluationEnable={canCurrentUserEvaluate()}
          />
        </div>
      );
    case "DP_ASSESSMENT_RUBRIC":
      return (
        <div className={classes.assessmentToolCard} ref={handleToolRef}>
          {assessmentToolText ? (
            <div className={"heading-4 p-6 pt-2 text-textSubtle pl-0"}>
              {assessmentToolText}
            </div>
          ) : null}
          {getEvaluationStatusLegend({
            assessmentToolType,
            isEvaluatedByStudent,
            isTeacherEvaluationEnable,
            userType,
          })}
          <CriteriaRubricAssessmentWrapper
            criteria={_.get(assessmentTool, "assessmentToolItem.criteria")}
            mode={"view"}
            {...assessmentToolsEvaluationDetails}
            isEvaluationEnable={canCurrentUserEvaluate()}
            customHeaderLabelClass={classes.toolTitle}
          />
        </div>
      );
    case "LEARNING_GOALS":
      return (
        <div className={classes.assessmentToolCard} ref={handleToolRef}>
          <div className={"heading-4 p-6 pt-2 text-textSubtle pl-0"}>
            {t("common:learning_goals")}
          </div>
          <PypEvaluationV2
            {...assessmentToolsEvaluationDetails}
            curriculumProgramType={curriculumProgramType}
            isMinimalDesign={true}
            showCriteriaDescriptorButton={true}
            showAllElements={userType !== "staff"}
            hideArchivedSubjectStandards={true}
            learningCourseId={learningCourseId}
          />
        </div>
      );
    case "SCORE":
      return (
        <div
          className={classes.assessmentToolCard}
          style={{ width: "100%" }}
          ref={handleToolRef}
        >
          <div className="heading-4 p-6 pt-2 text-textSubtle pl-2">
            {t("common:point_based_assessment")}
          </div>
          <div className={classes.scoreLabel}>
            {!_.isNil(scoreValue) && scoreValue >= 0
              ? `${scoreValue} ${t("common:out_of_with_label", {
                  label: maxScore,
                })}`
              : `${t("classRoom:not_graded")}`}
          </div>
        </div>
      );

    case ASSESSMENT_GRADE_BOUNDARY:
    case ASSESSMENT_AUTO_EVALUATE_CATEGORY_GRADE_BOUNDARY:
    case ASSESSMENT_AUTO_EVALUATE_TERM_GRADE_BOUNDARY:
    case ASSESSMENT_AUTO_EVALUATE_CUSTOM_GRADE_BOUNDARY:
      return (
        <div className={classes.assessmentToolCard} ref={handleToolRef}>
          <AssessmentIBScaleTool
            {...assessmentToolsEvaluationDetails}
            assessmentTool={_.get(assessmentTool, "assessmentToolItem")}
            isEvaluationEnable={canCurrentUserEvaluate()}
            toolStyles={styles.toolStyles}
          />
        </div>
      );
    case "ANALYSIS": {
      const { assessmentTools } = props;
      return (
        <div className={classes.assessmentToolCard} ref={handleToolRef}>
          <div
            className={"heading-4 text-subtle p-6 pt-2 text-textSubtle pl-0"}
          >
            {assessmentToolText}
          </div>
          <AIAnalysisScale
            {...props}
            name={props.fieldKey}
            assessmentTools={_.map(assessmentTools, assessmentTool => ({
              node: assessmentTool,
            }))}
            mode="view"
          />
        </div>
      );
    }

    case SCORE_BASED_RUBRIC:
    case STANDARD_BASED_RUBRIC: {
      const _updateEvaluationCell = params => {
        updateEvaluationCell({
          ...params,
          assessmentToolId: _.get(assessmentTool, "assessmentToolItem.id"),
          type: assessmentToolType,
        });
      };

      return (
        <div className={classes.assessmentToolCard} ref={handleToolRef}>
          <div className={"heading-4 p-6 pt-2 text-textSubtle pl-0"}>
            {assessmentToolText}
          </div>

          {getEvaluationStatusLegend({
            assessmentToolType,
            isEvaluatedByStudent,
            isTeacherEvaluationEnable,
            userType,
          })}

          <StandardRubric
            mode={"view"}
            assessmentTool={_.get(assessmentTool, "assessmentToolItem")}
            isEvaluatedByStudent={isEvaluatedByStudent}
            {...assessmentToolsEvaluationDetails}
            isEvaluationEnable={canCurrentUserEvaluate()}
            toolEvaluationResponses={currentToolEvaluationResponse}
            updateEvaluationCell={_updateEvaluationCell}
            type={assessmentToolType}
          />
        </div>
      );
    }
    case GRADE_SCALE_TOOL: {
      const displayName = _.get(
        assessmentTool,
        "assessmentToolItem.gradeScale.displayName"
      );

      const { ibPYPElementRatings } = assessmentToolsEvaluationDetails || {};

      const gradeScaleToolRating = _.find(ibPYPElementRatings, {
        ibPYPElementType: GRADE_SCALE_TOOL,
      });

      const value = _.get(
        gradeScaleToolRating,
        "academicCriteriaValue.abbreviation"
      );

      return (
        <div className={classes.assessmentToolCard} ref={handleToolRef}>
          <div className={classes.gradeScaleContainer}>
            <div className={"heading-4 p-6 pt-2 text-textSubtle pl-0"}>
              {`${displayName}: `}
              {!_.isEmpty(value) ? (
                <span className={classes.commentText}>{value}</span>
              ) : (
                "-"
              )}
            </div>

            {!_.isEmpty(value) ? (
              <AssessmentGradeScaleTool
                mode={"view"}
                assessmentTool={_.get(assessmentTool, "assessmentToolItem")}
                {...assessmentToolsEvaluationDetails}
              />
            ) : null}
          </div>
        </div>
      );
    }
    default:
      return null;
  }
});

const mapStateToProps = (state, ownProps) => {
  const { assessmentTools } = ownProps;

  const { isScoreEnabled, maxScore } = getScoreDetailsMemoize({
    assessmentTools: {
      edges: _.map(assessmentTools, tool => ({ node: tool })),
    },
  });

  const isScoringEnabled =
    _.get(
      ownProps?.assessmentToolsEvaluationDetails,
      "isTeacherEvaluationEnabled"
    ) &&
    isScoreEnabled &&
    maxScore;

  let scoreValue;
  const ibPYPElementRatings = _.get(
    ownProps?.assessmentToolsEvaluationDetails,
    "ibPYPElementRatings"
  );

  const isSharedWithStudent = _.get(
    ownProps,
    "assessmentToolsEvaluationDetails.isSharedWithStudent",
    false
  );

  if (isScoringEnabled) {
    scoreValue =
      getUserType(state) == "staff" || isSharedWithStudent
        ? getScoreValueElementRatings({
            ibPYPElementRatings,
          })
        : null;
  }

  const visibleIbPYPElementRatings =
    getUserType(state) == "staff" || isSharedWithStudent
      ? ibPYPElementRatings
      : [];

  const updatedAssessmentToolsEvaluationDetails = {
    ..._.get(ownProps, "assessmentToolsEvaluationDetails", {}),
    ibPYPElementRatings: visibleIbPYPElementRatings,
  };

  return {
    isData: true,
    isLoading: false,
    scoreValue,
    maxScore,
    userType: _.get(state, "login.userInfo.user_type", ""),
    parentId: _.get(
      ownProps,
      "assessmentToolsEvaluationDetails.parentId",
      null
    ),
    parentType: _.get(
      ownProps,
      "assessmentToolsEvaluationDetails.parentType",
      null
    ),
    assessmentToolsEvaluationDetails: updatedAssessmentToolsEvaluationDetails,
    isSharedWithStudent,
  };
};

const mapActionCreators = {
  updateField,
  handleAssessmentToolResponseInCache,
};
const GrouppedAssessmentToolsViewer = props => {
  const assessmentToolsRefs = useRef([]);
  const { assessmentToolsData = [], isWorksheet } = props;
  useEffect(() => {
    const mediaAttachments = _.get(props, "mediaAttachments.attachments", []);
    const selectedAssessmentTool = _.get(props, [
      "assessmentTools",
      props?.activeIndex - _.size(mediaAttachments, 0),
    ]);
    const ref = assessmentToolsRefs.current[selectedAssessmentTool?.id];
    const isMobile = matchMedia("(max-width: 1024px)").matches;
    if (!_.isNil(ref) && !_.isEmpty(ref) && !isMobile) {
      ref.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [props?.activeIndex]);

  return (
    <div className={classes.groupContainer}>
      {_.map(_.get(props, "assessmentTools", []), assessmentTool => {
        const assessmentToolData = _.find(
          assessmentToolsData,
          item =>
            item?.id == assessmentTool?.id &&
            item?.assessmentToolType == assessmentTool?.assessmentToolType
        );

        const assessmentId = _.get(assessmentTool, "id");
        const assessmentToolType = _.get(assessmentTool, "assessmentToolType");
        const assessmentEntityType =
          assessmentToolType != "LEARNING_GOALS"
            ? _.get(
                _.find(ASSESSMENT_TOOLS, { type: assessmentToolType }),
                "entityType",
                ""
              )
            : "LEARNING_GOALS";
        return isWorksheet && assessmentToolType == "SCORE" ? null : (
          <div className={classes.assessmentToolCardWrapper} key={assessmentId}>
            <AssessmentToolViewer
              {...props}
              mediaDataAttachment={assessmentTool}
              assessmentTool={assessmentToolData}
              assessmentToolsRefs={assessmentToolsRefs}
              assessmentId={assessmentId}
              assessmentEntityType={assessmentEntityType}
              assessmentToolType={assessmentToolType}
            />
          </div>
        );
      })}
    </div>
  );
};

const getPublicLinkProps = props => {
  const extraProps = { isPublicURL: false };
  if (
    _.includes(_.get(props, "ownProps.location.pathname", ""), "/public/post/")
  ) {
    extraProps.isPublicURL = true;
  }
  return extraProps;
};
export default compose(
  connect(mapStateToProps, mapActionCreators),
  withQueryParamsBasedInterface({ cb: getPublicLinkProps }),
  graphql(getAssessmentToolsDetailsByStudentAssignmentIdQuery, {
    name: "getAssessmentToolsDetailsByStudentAssignmentId",
    options: ({ parentId }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: parentId },
    }),
    skip: props =>
      _.get(props, "isPublicURL") ||
      _.isNil(props.parentId) ||
      _.isEmpty(props.parentId) ||
      _.isEqual(props?.parentType, QUESTION_SUBMISSION),
    props({
      getAssessmentToolsDetailsByStudentAssignmentId,
      ownProps: { isData, isLoading, parentId },
    }) {
      const queryData = getAssessmentToolsDetailsByStudentAssignmentIdFromCache(
        {
          id: parentId,
        }
      );
      const networkStatus = _.get(
        getAssessmentToolsDetailsByStudentAssignmentId,
        "networkStatus"
      );
      return {
        assessmentToolsData: _.map(
          _.get(queryData, "assignment.content.assessmentTools.edges"),
          toolEdge => _.get(toolEdge, "node")
        ),
        isData: !_.isEmpty(queryData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  graphql(getAssessmentToolsDetailsByPostIdQuery, {
    name: "getAssessmentToolsDetailsByPostId",
    options: ({ params = {} }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: _.get(params, "postId") },
    }),
    skip: props =>
      !_.get(props, "isPublicURL") || _.isEmpty(_.get(props, "params.postId")),
    props({
      getAssessmentToolsDetailsByPostId,
      ownProps: { isData, isLoading, params },
    }) {
      const queryData = getAssessmentToolsDetailsByPostIdFromCache({
        id: _.get(params, "postId"),
      });
      const networkStatus = _.get(
        getAssessmentToolsDetailsByPostId,
        "networkStatus"
      );
      const assignmentItem = _.get(
        _.first(_.get(queryData, "items")),
        "item",
        {}
      );
      return {
        assessmentToolsData: _.map(
          _.get(assignmentItem, "assignment.content.assessmentTools.edges"),
          toolEdge => _.get(toolEdge, "node")
        ),
        isData: !_.isEmpty(assignmentItem) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  graphql(getAssessmentToolsByQuestionSubmissionIdQuery, {
    name: "getAssessmentToolsByQuestionSubmissionId",
    options: ({ parentId }) => ({
      fetchPolicy: "cache-and-network",
      variables: { id: parentId },
    }),
    skip: props =>
      _.get(props, "isPublicURL") ||
      _.isNil(props.parentId) ||
      !_.isEqual(props?.parentType, QUESTION_SUBMISSION),
    props({
      getAssessmentToolsDetailsByStudentAssignmentId,
      ownProps: { isData, isLoading, parentId },
    }) {
      const queryData = getAssessmentToolsByQuestionSubmissionIdFromCache({
        id: parentId,
      });
      const networkStatus = _.get(
        getAssessmentToolsDetailsByStudentAssignmentId,
        "networkStatus"
      );
      return {
        assessmentToolsData: _.map(
          _.get(queryData, "question.assessmentTools.edges"),
          toolEdge => _.get(toolEdge, "node")
        ),
        isData: !_.isEmpty(queryData) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  withLoader
)(GrouppedAssessmentToolsViewer);
AssessmentToolViewer.displayName = "AssessmentToolViewer";
